import { Injectable, signal } from '@angular/core';

export interface MinimalSelection {
  country: string;
  city: string;
}

export interface LocationSelection extends MinimalSelection {
  cityId?: number | null;
  t0Available?: boolean;
  t3Available?: boolean;
  t6Available?: boolean;
  raw?: unknown;
}

export const LOCATION_STORAGE_KEYS = {
  legacy: 'selectedLocation',
  t0: 'selectedLocation_t0',
  t3: 'selectedLocation_t3',
  t6: 'selectedLocation_t6',
  economy: 'selectedLocation_economy',
  fileUpload: 'selectedLocation_fileupload'
} as const;

@Injectable({ providedIn: 'root' })
export class LocationSelectionService {
  private readonly _selected = signal<LocationSelection | null>(null);
  private readonly _cityLoading = signal<boolean>(false);

  readonly selected = this._selected.asReadonly();
  readonly cityLoading = this._cityLoading.asReadonly();

  getSelected(): LocationSelection | null {
    return this._selected();
  }

  /**
   * Sets the active selection and persists it to the given storage keys.
   * Locked tiers (T3/T6/economy/file-upload) must NOT include the legacy/T0 key.
   */
  setSelected(value: LocationSelection | null, storageKeys: string[] = [LOCATION_STORAGE_KEYS.legacy]): void {
    this._selected.set(value ? { ...value } : null);

    for (const key of storageKeys) {
      try {
        if (value) {
          sessionStorage.setItem(key, JSON.stringify(value));
        } else {
          sessionStorage.removeItem(key);
        }
      } catch {}
    }
  }

  /** Reads a persisted selection for a specific storage key without mutating the active signal. */
  readFromSession(storageKey: string): LocationSelection | null {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as LocationSelection;
      return parsed?.city ? parsed : null;
    } catch {
      return null;
    }
  }

  setCityLoading(value: boolean): void {
    this._cityLoading.set(!!value);
  }

  clear(): void {
    this._selected.set(null);
    this._cityLoading.set(false);
  }
}